import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'
import { Tooltip } from './Tooltip'

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'title'
> {
  /** The button's accessible name, and its tooltip unless `tooltip` is given. */
  label: string
  tooltip?: ReactNode
  /** Why the button is disabled; its tooltip while `disabled`. */
  disabledReason?: ReactNode
  /** Classes for the tooltip's wrapper, the box in the layout (flex sizing, absolute positioning). */
  wrapperClassName?: string
  tooltipAlign?: 'left' | 'right'
  tooltipPlacement?: 'default' | 'right-start' | 'right'
}

/**
 * A button named by `label` rather than visible text: its `aria-label`, shown
 * in the app's tooltip. It carries no styling of its own.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    tooltip,
    disabledReason,
    wrapperClassName,
    tooltipAlign,
    tooltipPlacement,
    type = 'button',
    className,
    children,
    ...props
  },
  ref
) {
  const reason = props.disabled ? disabledReason : undefined
  const explainsDisabled = reason !== undefined && reason !== null && reason !== ''

  return (
    <Tooltip
      variant="label"
      content={explainsDisabled ? reason : (tooltip ?? label)}
      align={tooltipAlign}
      placement={tooltipPlacement}
      focusable={explainsDisabled}
      className={clsx(explainsDisabled && 'cursor-not-allowed', wrapperClassName)}
    >
      <button
        ref={ref}
        type={type}
        aria-label={label}
        // A disabled button swallows the pointer, so the wrapper takes the hover.
        className={clsx(className, explainsDisabled && 'pointer-events-none')}
        {...props}
      >
        {children}
      </button>
    </Tooltip>
  )
})
